// src/components/Auth/AuthPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './AuthPage.module.css'; // Ensure this path is correct

import HomepageBackground from '../../assets/backgrounds/AuthBg.png';
import Navbar from './Navbar';
import posthog from 'posthog-js'; // Import PostHog

// Import icons for onboarding form fields
import { FaEnvelope, FaLock, FaKey } from "react-icons/fa";
import {
    loginUser, signupUser, confirmSignup, resendConfirmationCode, redirectToGoogleLogin,
    requestPasswordReset, confirmPasswordReset,redirectToAmazonLogin,
    fetchUserProfile
    // IMPORTANT: You will eventually need an API function here to save onboarding data
    // import { saveOnboardingData } from '../../services/userApi'; // Example function name
} from '../../services/authApi'; // Make sure path is correct
import AuthContext from '../../context/AuthContext'; // Make sure path is correct
import { captureReferralCodeFromUrl } from '../../services/referralStorage';


// Helper function to handle post-auth redirect
const handlePostAuthRedirect = (navigate, location) => {
    const urlParams = new URLSearchParams(location.search);
    const source = urlParams.get('source');

    if (source === 'unlock_analysis') {
        navigate('/dashboard/listing-score');
    } else if (source === 'pricing') {
        const credits = urlParams.get('credits') || '';
        navigate('/dashboard/plans' + (credits ? '?credits=' + credits : ''));
    } else {
        navigate('/dashboard');
    }
};

const AuthPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const authContext = useContext(AuthContext);

   
    const validatePassword = (value) => {
    const isLongEnough = value.length >= 8;
    const hasUppercase = /[A-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSymbol = /[^A-Za-z0-9]/.test(value);

    if (!isLongEnough || !hasUppercase || !hasNumber || !hasSymbol) {
      setError('8 Characters, 1 Capital, 1 Number, 1 Symbol');
    } else {
      setError('');
    }
  };

    // --- General Auth State ---
    const [isLoginMode, setIsLoginMode] = useState(true);
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isResetLoading, setIsResetLoading] = useState(false);
    const [isConfirmationLoading, setIsConfirmationLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showPassword, setShowPassword] = useState(false); // State for password visibility (implement toggle if needed)
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    

    // --- Confirmation State ---
    const [needsConfirmation, setNeedsConfirmation] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState('');
    const [usernameForConfirmation, setUsernameForConfirmation] = useState(''); // Store username used for signup/confirmation

    // --- Forgot Password State ---
    const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
    const [needsPasswordResetConfirmation, setNeedsPasswordResetConfirmation] = useState(false);
    const [resetIdentifier, setResetIdentifier] = useState(''); // Email used for reset
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    // --- >>> NEW: Onboarding State <<< ---
    const [showOnboardingForm, setShowOnboardingForm] = useState(false);
    const [onboardingFullName, setOnboardingFullName] = useState("");
    const [onboardingCompanyName, setOnboardingCompanyName] = useState("");
    const[onboardingLocation, setonboardingLocation]=useState("");
    // Check URL parameters on component mount to set appropriate mode
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const source = urlParams.get('source');
        const mode = urlParams.get('mode');

        if (source === 'unlock_analysis' || mode === 'signup') {
            setIsLoginMode(false); // Set to signup mode
        }

        // Persist any inbound `?ref=EB-XXXXXX` so the eventual signup call
        // (email confirm OR Google OAuth redirect) can forward it to the backend.
        captureReferralCodeFromUrl(location.search);
    }, [location.search]);

    const [onboardingPhoneNumber, setOnboardingPhoneNumber] = useState("");
    // --- >>> END: Onboarding State <<< ---

    // --- Clear form state helper ---
    const resetAuthState = (switchToLogin = true) => {
        setError(null);
        setSuccessMessage(null);
        setUsername('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setConfirmationCode('');
        setNeedsConfirmation(false);
        // Keep usernameForConfirmation as it might be needed if user cancels onboarding
        // setUsernameForConfirmation('');
        setIsForgotPasswordMode(false);
        setNeedsPasswordResetConfirmation(false);
        setResetIdentifier('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');
        setIsLoginMode(switchToLogin); // Control login/signup mode switch

        // --- >>> NEW: Reset Onboarding State <<< ---
        setShowOnboardingForm(false); // Hide onboarding form
        setOnboardingFullName("");
        setOnboardingCompanyName("");
        setOnboardingPhoneNumber("");
        // --- >>> END: Reset Onboarding State <<< ---
    };

        // Toggle password section visibility
    const togglePasswordFields = () => {
        setShowPasswordFields(!showPasswordFields);
    };

    // --- Mode Switcher ---
    const switchModeHandler = () => {
        resetAuthState(!isLoginMode); // Reset state and toggle login mode
    };

    // --- Go To Forgot Password Mode ---
    const forgotPasswordHandler = () => {
        resetAuthState(false); // Reset state but don't switch to login yet
        setIsForgotPasswordMode(true);
        setIsLoginMode(false); // Ensure not in login mode
    };

    // --- Go Back To Login (from Forgot/Confirm/Onboarding) ---
    const backToLoginHandler = () => {
         resetAuthState(true); // Reset all states and switch back to login
    };

    // --- Handle Email/Password Form Submission (Login/Signup) ---
    const submitHandler = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setIsLoading(true);
        setNeedsConfirmation(false); // Reset confirmation need
        setShowOnboardingForm(false); // Ensure onboarding is hidden when trying to login/signup normally

        if (isLoginMode) {
            // ===== LOGIN LOGIC =====
            try {
                // Step 1: Authenticate with Cognito
                const loginResponse = await loginUser(email, password);
                const { access_token, id_token, refresh_token } = loginResponse;
                if (!access_token || !id_token) throw new Error("Login failed: Missing essential tokens.");

                // Step 2: Fetch profile data (which should include onboarding status later)
                // Inner try-catch for fetching profile during LOGIN
                try {
                    const profileData = await fetchUserProfile(access_token);

                    // Step 3: Update Auth Context
                    authContext.login(access_token, id_token, refresh_token);
                    authContext.updateUser(profileData); // Update context with latest profile

                    // PostHog: Track Login Event with user info
                    posthog.identify(profileData?.username || profileData?.sub, {
                        email: email,
                        name: profileData?.name || profileData?.username || '',
                        $initial_referring_domain: document.referrer
                    });

                    posthog.capture('user_login', {
                        distinct_id: profileData?.username || profileData?.sub,
                        login_method: 'email',
                        login_timestamp: new Date().toISOString(),
                        session_start: new Date().toISOString()
                    });

                    // ✅ Store session start time for calculating duration on logout
                    localStorage.setItem('sessionStartTime', Date.now().toString());


                    // Check if onboarding is complete based on the flag
                    if (profileData?.onboarding_complete === true && profileData?.questionnaire_complete === false) {
                        // Onboarding done but questionnaire not yet completed — show it once
                        navigate('/onboarding');
                    } else if (profileData?.onboarding_complete === true) {
                        // User has completed onboarding and questionnaire
                        handlePostAuthRedirect(navigate, location);
                    } else {
                        // Onboarding not complete, direct to onboarding form
                        setShowOnboardingForm(true);
                        setOnboardingFullName(profileData?.name || '');
                        setUsernameForConfirmation(profileData?.username || profileData?.sub);
                    }

                } catch (profileError) {
                    // Handle *profile fetch* error after successful login
                    setError("Login succeeded, but couldn't load profile details. Please try refreshing.");
                    // Log user in anyway
                    authContext.login(access_token, id_token, refresh_token);
    
                    // PostHog: Still track login but with limited info
                    posthog.capture('user_login', {
                        login_method: 'email',
                        login_timestamp: new Date().toISOString(),
                        error: 'profile_fetch_failed',
                        session_start: new Date().toISOString()
                    });

                    // Navigate to dashboard, ProtectedRoute might redirect if context is crucial and missing
                    handlePostAuthRedirect(navigate, location);
                }
                // End inner try-catch for profile fetch

            } catch (err) {
                // Handle *login* API errors (wrong password, user not confirmed, etc.)
                const message = err.message || err.data?.error || err.data?.message || 'Authentication failed.';
                setError(message);

                // PostHog: Track failed login attempt
                posthog.capture('login_failed', {
                    reason: message,
                    email: email // This is safe to track for analytics
                });

                // If Cognito says user is not confirmed
                if (err.data?.needsConfirmation) {
                    setNeedsConfirmation(true);
                    // Attempt to get username associated with the login attempt
                    setUsernameForConfirmation(err.data?.username || username || email); // Use state 'username' or 'email' as fallback
                    setEmail(email); // Keep email for potential resend
                    setPassword(''); // Clear password field
                    setIsLoginMode(true); // Keep in login mode contextually, but show confirmation form
                    setIsForgotPasswordMode(false);
                }
            } finally {
                setIsLoading(false);
            }
            // ===== END LOGIN LOGIC =====

        } else {
            // ===== SIGNUP LOGIC =====
            if (password !== confirmPassword) { setError('Passwords do not match.'); setIsLoading(false); return; }

                // PostHog: Track signup validation error
                posthog.capture('signup_validation_error', {
                    error_type: 'passwords_mismatch'
                });

            try {
                const response = await signupUser(username, email, password);
                setNeedsConfirmation(true);
                setUsernameForConfirmation(response.username); // Use username returned from backend *IMPORTANT*
                setEmail(email); // Keep email for display/resend logic
                setError(null);
                setSuccessMessage("Account creation initiated. Check your email for a code.");
                setPassword(''); setConfirmPassword('');
                setIsForgotPasswordMode(false); // Ensure not in forgot password mode

                // PostHog: Track Signup Event
                posthog.capture('user_signup', {
                    distinct_id: response.username,
                    email: email,
                    username: username,
                    signup_timestamp: new Date().toISOString()
                });

            } catch (err) {
                const message = err.message || err.data?.error || err.data?.message || 'Signup failed.';
                setError(message);

                // PostHog: Track signup failure
                posthog.capture('signup_failed', {
                    reason: message,
                    email: email
                });

                // Handle edge case where signup fails but indicates confirmation is needed anyway (e.g., UsernameExists)
                if (err.data?.needsConfirmation && err.data?.username) {
                    setNeedsConfirmation(true);
                    setUsernameForConfirmation(err.data.username); // Use username from error response
                    setEmail(email); // Keep email
                    setSuccessMessage(null); // Clear initial success message if error occurs
                    setPassword(''); setConfirmPassword('');
                    setIsForgotPasswordMode(false);
                }
            } finally {
                setIsLoading(false);
            }
            // ===== END SIGNUP LOGIC =====
        }
    };

    // --- >>> Confirmation Handler (MODIFIED FOR ONBOARDING) <<< ---
    const confirmHandler = async (event) => {
        event.preventDefault();
        if (!usernameForConfirmation || !confirmationCode) {
            setError("Please enter the confirmation code.");
            return;
        }
        setError(null);
        setSuccessMessage(null);
        setIsConfirmationLoading(true);
        
        try {
            // Use the stored username from signup/error handling
            await confirmSignup(usernameForConfirmation, confirmationCode);

            // PostHog: Track successful account confirmation
            posthog.capture('account_confirmed', {
                distinct_id: usernameForConfirmation,
                confirmation_timestamp: new Date().toISOString()
            });
            
            // Show success message but don't show onboarding form
            setSuccessMessage("Account confirmed successfully! You can now login to your account.");
            setNeedsConfirmation(false);    // Hide confirmation form
            setShowOnboardingForm(false);   // Don't show the onboarding form
            setError(null);                 // Clear any previous confirmation errors
            setIsLoginMode(true);           // Switch to login mode
            
            // Keep email if available to make login easier
            // Reset password fields
            setPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError(err.message || err.data?.error || 'Confirmation failed.');
            setShowOnboardingForm(false);   // Ensure onboarding is hidden on error

            // PostHog: Track confirmation failure
            posthog.capture('confirmation_failed', {
                distinct_id: usernameForConfirmation,
                reason: err.message || err.data?.error || 'Confirmation failed.'
            });

        } finally {
            setIsConfirmationLoading(false);
        }
    };
     // --- >>> END Confirmation Handler Modification <<< ---


     // --- Resend Confirmation Code Handler (Uses usernameForConfirmation) ---
     const resendCodeHandler = async () => {
         if (!usernameForConfirmation) {
             setError("Username not available to resend code. Try logging in or signing up again."); return;
         }
         setError(null); setSuccessMessage(null); setIsLoading(true);
         try {
             await resendConfirmationCode(usernameForConfirmation); // Backend needs username
             setSuccessMessage("A new confirmation code has been sent to your email.");
         } catch (err) {
             setError(err.message || err.data?.error || 'Failed to resend code.');
         } finally { setIsLoading(false); }
    };

    // --- Handle Forgot Password Request ---
    const handleForgotPasswordRequest = async (event) => {
        event.preventDefault();
        if (!email) { setError("Please enter your email address."); return; }
        setError(null); setSuccessMessage(null); setIsLoading(true);
        try {
            const response = await requestPasswordReset(email);
            setSuccessMessage(response.message || "Password reset initiated. Check your email for a code.");
            setResetIdentifier(email); // Store the email used for reset confirmation
            setNeedsPasswordResetConfirmation(true); // Show the next step form
            setError(null);
        } catch (err) {
            setError(err.message || err.data?.error || 'Failed to initiate password reset.');
            setSuccessMessage(null);
        } finally { setIsLoading(false); }
    };

    // --- Handle Confirm Password Reset ---
    const handleConfirmPasswordReset = async (event) => {
        event.preventDefault();
        if (!resetCode || !newPassword || !confirmNewPassword) { setError("Please fill in the code, new password, and confirmation."); return; }
        if (newPassword !== confirmNewPassword) { setError("New passwords do not match."); return; }
        if (newPassword.length < 8) { setError("New password must be at least 8 characters."); return; }
        setError(null); setSuccessMessage(null); setIsResetLoading(true);
        try {
            const response = await confirmPasswordReset(resetIdentifier, resetCode, newPassword); // Use resetIdentifier (email)
            setSuccessMessage(response.message || "Password reset successfully! Please log in.");
            resetAuthState(true); // Reset state and go back to login mode
        } catch (err) {
            setError(err.message || err.data?.error || 'Failed to reset password.');
            setSuccessMessage(null);
        } finally { setIsResetLoading(false); }
    };

    // --- Google Login Handler ---
    const handleGoogleLoginClick = () => {
        setError(null); setSuccessMessage(null); setIsLoading(true);
        redirectToGoogleLogin(); // Redirects user, callback handled by useEffect
    };
        
        // Track page unload to capture session duration
        useEffect(() => {
            const handleBeforeUnload = () => {
                if (authContext.isLoggedIn && authContext.user) {
                    // Track session end on page unload
                    posthog.capture('session_ended', {
                        distinct_id: authContext.user.username || authContext.user.sub,
                        session_end: new Date().toISOString()
                    });
                }
            };
            
            window.addEventListener('beforeunload', handleBeforeUnload);
            
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }, [authContext.isLoggedIn, authContext.user]);

    // --- Amazon Login Handler ---
    const handleAmazonLoginClick = () => {
        setError(null); setSuccessMessage(null); setIsLoading(true);
        redirectToAmazonLogin(); // This calls the function you've already defined in authApi.js
    };

    // --- Effect for OAuth Callback & Initial Redirect ---
    // Handles redirect back from Google/Cognito Hosted UI and checks onboarding
    useEffect(() => {
        const processAuthCallback = async () => {
            const hash = window.location.hash;
            // Only process if there's actually a hash potentially containing tokens
            if (hash && (hash.includes('id_token=') || hash.includes('access_token=') || hash.includes('error='))) {

                const params = new URLSearchParams(hash.substring(1)); // Remove '#'
                const idToken = params.get('id_token');
                const accessToken = params.get('access_token');
                const refreshToken = params.get('refresh_token'); // May be null
                const errorParam = params.get('error');

                 // Clean the hash from the URL immediately after parsing
                window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

                if (idToken && accessToken) {
                    setIsLoading(true); // Show loading indicator while fetching profile

                    try {
                        const profileData = await fetchUserProfile(accessToken);

                        // Remove temporary bypass and use actual onboarding status
                        const onboardingComplete = profileData?.questionnaire_complete ?? false;

                        // Log user into context first
                        // Get login provider from localStorage (it's set in redirectToAmazonLogin or redirectToGoogleLogin)
                        const loginProvider = localStorage.getItem('loginProvider') || 
                            localStorage.getItem('pendingLoginProvider');

// Log user into context first
authContext.login(accessToken, idToken, refreshToken, loginProvider);
                        // Update context with full profile data
                        authContext.updateUser(profileData);

                        if (onboardingComplete) {
                            handlePostAuthRedirect(navigate, location);
                        } else {
                            navigate('/onboarding');
                        }
                        // End Onboarding Check

                    } catch (profileError) {
                        setError("Login succeeded, but couldn't load profile details. Please try refreshing.");
                        authContext.login(accessToken, idToken, refreshToken); // Still log them in
                        handlePostAuthRedirect(navigate, location); // Navigate anyway

                    } finally {
                        // Ensure loading is turned off unless onboarding form is shown
                        if (!showOnboardingForm) {
                            setIsLoading(false);
                        }
                    }

                } else if (errorParam) {
                    const errorMessage = params.get('error_description') || 'OAuth login failed.';
                    setError(`Login failed: ${errorMessage}`);
                }
            } else {
                // No relevant hash, check if already logged in (e.g., page refresh)
                if (authContext.isLoggedIn) {
                    // Remove temporary bypass and use actual onboarding status
                    const onboardingComplete = authContext.user?.questionnaire_complete ?? false;

                    if (onboardingComplete) {
                        handlePostAuthRedirect(navigate, location);
                    } else if (authContext.user) {
                         // If user context exists but onboarding isn't complete, go to onboarding flow
                         navigate('/onboarding');
                         // Pre-fill if needed from context
                        //  setOnboardingFullName(authContext.user?.name || authContext.user?.username || '');
                        // setUsernameForConfirmation(authContext.user?.username || authContext.user?.sub);
                    } 
                } 
            }
        };

        processAuthCallback(); // Call the async function

    // Dependencies: Re-run if location changes (hash changes), or if auth state changes,
    // or if we explicitly toggle the onboarding form.
    }, [navigate, location, authContext, showOnboardingForm]); // Keep authContext dependency


    // --- >>> NEW: Onboarding Submit Handler <<< ---
    const handleOnboardingSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage(null);
    
        // Basic Frontend Validation
        if (!onboardingFullName.trim()) { setError("Please enter your full name."); return; }
        if (!onboardingCompanyName.trim()) { setError("Please enter your company name."); return; }
        if (!onboardingPhoneNumber.trim()) { setError("Please enter your phone number."); return; }
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(onboardingPhoneNumber.replace(/\D/g, ''))) { setError("Please enter a valid phone number (exactly 10 digits)."); return; }
    
        // 1. Fetch UUID and username from localStorage
        const uuid = authContext.user?.sub || null;
        const username = usernameForConfirmation || authContext.user?.username || authContext.user?.cognito_username || null;
        
        // Check if UUID and username exist
        if (!uuid || !username) {
            setError("Authentication information missing. Please log in again.");
            setIsLoading(false);
            return;
        }
    
        setIsLoading(true); // Start loading indicator
        const onboardingData = {
            fullName: onboardingFullName.trim(),
            companyName: onboardingCompanyName.trim(),
            location: onboardingLocation.trim(),
            phoneNumber: onboardingPhoneNumber.trim(),
            uuid: uuid,
            username: username,
        };
    
        try {
            // 2. Call the backend API with the onboarding data
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_URL}/auth/handle-onboarding-details`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(onboardingData)
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to save onboarding information');
            }
    
            const responseData = await response.json();

            // PostHog: Track onboarding completion
            posthog.capture('onboarding_completed', {
                distinct_id: username,
                company_name: onboardingCompanyName,
                location: onboardingLocation,
                onboarding_timestamp: new Date().toISOString()
            });
            
            // Update user properties in PostHog
            posthog.people.set({
                company_name: onboardingCompanyName,
                location: onboardingLocation,
                onboarding_complete: true
            });

            // Update user context with onboarding complete flag
            authContext.updateUser({
                ...authContext.user,
                onboarding_complete: true,
                onboarding_form_shown: true
            });

            // Redirect to dashboard after successful onboarding
            handlePostAuthRedirect(navigate, location);

    
        } catch (err) {
            // Use error message from backend API call if available, otherwise generic
            setError(err.message || err.data?.error || 'Failed to save onboarding information. Please try again.');
            // Keep the form visible with the error message

            // PostHog: Track onboarding failure
            posthog.capture('onboarding_failed', {
                distinct_id: username,
                reason: err.message || 'API error',
            });

        } finally {
            setIsLoading(false); // Stop loading indicator
        }
    };
    // --- >>> END: Onboarding Submit Handler <<< ---


    // --- Determine Current View To Render ---
    let content;

    // --- Original Content Logic (Forgot Password, Confirmation, Login/Signup) ---
    if (isForgotPasswordMode) {
        if (needsPasswordResetConfirmation) {
            // --- FORM: Enter Code & New Password ---
            content = (
                <>
                    <h2>Reset Your Password</h2>
                    {error && <p className={styles.errorText}>{error}</p>}
                    {successMessage && <p className={styles.successText}>{successMessage}</p>}
                    <p className={styles.confirmationInstruction}>
                    Enter the code sent to <strong>{resetIdentifier}</strong> <br></br> and choose your new password.
                    </p>
                    <form onSubmit={handleConfirmPasswordReset} className={styles.confirmationForm}>
                        <div className={styles.inputGroup}>  <input type="text" id="resetCode" placeholder="Reset Code" value={resetCode} onChange={(e) => setResetCode(e.target.value)} required autoFocus inputMode="numeric" maxLength="6" aria-label="Password Reset Code" disabled={isResetLoading}/> </div>
                        <div className={styles.inputGroup}>  <input type={showPassword ? 'text' : 'password'} id="newPassword" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength="8" required aria-label="New Password" disabled={isResetLoading}/> 
                        {password && (
                                <button
                                type="button"
                                className={styles.visibilityToggle}
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    {showPassword ? (
                                    <>
                                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                    </>
                                    ) : (
                                    <>
                                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                        <line x1="2" x2="22" y1="2" y2="22"></line>
                                    </>
                                    )}
                                </svg>
                                </button>
                            )}
                        </div>

                        <div className={styles.inputGroup}> <input type={showPassword ? 'text' : 'password'} id="confirmNewPassword" placeholder="Confirm New Password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} minLength="8" required aria-label="Confirm New Password" disabled={isResetLoading}/> 
                            {password && (
                                <button
                                type="button"
                                className={styles.visibilityToggle}
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    {showPassword ? (
                                    <>
                                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                    </>
                                    ) : (
                                    <>
                                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                        <line x1="2" x2="22" y1="2" y2="22"></line>
                                    </>
                                    )}
                                </svg>
                                </button>
                            )}
                        </div>
                        {/* TODO: Add show/hide password toggle */}
                        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isResetLoading}> {isResetLoading ? 'Resetting...' : 'Reset Password'} </button>
                    </form>
                    <div className={styles.secondaryActions}> <button type="button" className={`${styles.btn} ${styles.btnTextLink}`} onClick={backToLoginHandler} disabled={isResetLoading}> Back to Login </button> </div>
                </>
            );
        } else {
            // --- FORM: Enter Email to Request Reset ---
            content = (
                 <>
                    <h2>Forgot Your Password?</h2>
                    {error && <p className={styles.errorText}>{error}</p>}
                    {successMessage && <p className={styles.successText}>{successMessage}</p>}
                    <p className={styles.confirmationInstruction}> Enter the email address associated with your Ecombuddha account. </p>
                    <form onSubmit={handleForgotPasswordRequest} className={styles.confirmationForm}>
                        <div className={styles.inputGroup}>  <input type="email" id="email-forgot" placeholder="Registered email address" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus aria-label="Email Address for Password Reset" disabled={isLoading}/> </div>
                        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isLoading}> {isLoading ? 'Sending...' : 'Send Reset Code'} </button>
                    </form>
                     <div className={styles.secondaryActions}> <button type="button" className={`${styles.btn} ${styles.btnTextLink}`} onClick={backToLoginHandler} disabled={isLoading}> Back to Login </button> </div>
                 </>
            );
        }
    } else if (needsConfirmation) {
        // --- FORM: Confirm Signup Code ---
        content = (
           <>
               <h3 className={styles.confirmationTitle}>Confirm your Account</h3>
               {successMessage && <div className={styles.successBanner}>{successMessage}</div>}
               {error && <p className={styles.errorText}>{error}</p>}
               <p className={styles.confirmationInstruction}> Please enter the code sent to <strong className={styles.emailHighlight}>{email || 'your email'}</strong><br/>for the user name {usernameForConfirmation || 'provided'}. </p>
               <form onSubmit={confirmHandler} className={styles.confirmationForm}>
                   <div className={styles.codeInputContainer}>
                       {[0, 1, 2, 3, 4, 5].map((index) => (
                           <input
                               key={index}
                               type="text"
                               className={styles.codeInput}
                               value={confirmationCode[index] || ''}
                               onChange={(e) => {
                                   const newCode = confirmationCode.split('');
                                   newCode[index] = e.target.value;
                                   setConfirmationCode(newCode.join(''));
                                   // Auto-focus next input
                                   if (e.target.value && index < 5) {
                                       e.target.nextSibling?.focus();
                                   }
                               }}
                               
                               maxLength="1"
                               inputMode="numeric"
                               autoFocus={index === 0}
                               disabled={isConfirmationLoading}
                           />
                       ))}
                   </div>
                   <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isConfirmationLoading}> {isConfirmationLoading ? 'Confirming...' : 'Confirm Account'} </button>
               </form>
               <div className={styles.resendContainer}>
                    <button type="button" className={styles.resendLink} onClick={resendCodeHandler} disabled={isConfirmationLoading || !usernameForConfirmation}> Resend code </button>
                    <button type="button" className={styles.resendLink} onClick={backToLoginHandler} disabled={isConfirmationLoading}> Back to Login </button>
               </div>
           </>
        );
    } else {
        // --- FORM: Login or Signup ---
        content = (
             <>
                 {/* <h2>{isLoginMode ? 'Sign in to Ecombuddha' : 'Takes only a few moments to register'}</h2> */}
                 {error && <p className={styles.errorText}>{error}</p>}
                 {successMessage && <p className={styles.successText}>{successMessage}</p>} {/* Show success message after onboarding submit */}
                 <form onSubmit={submitHandler}>
                     {/* Signup specific field */}
                     {!isLoginMode && ( 
                         <div className={styles.inputGroup}>
                             <label htmlFor="username" className={styles.inputLabel}>Username</label>
                             <input type="text" id="username" placeholder="Choose a Username" value={username} onChange={(e) => setUsername(e.target.value)} required aria-label="Choose a Username" disabled={isLoading}/>
                         </div> 
                     )}
                     {/* Common fields */}
                     <div className={styles.inputGroup}>
                         <label htmlFor="email" className={styles.inputLabel}>Email Address</label>
                         <input type="email" id="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} required aria-label="Email Address" disabled={isLoading}/>
                     </div>
    
                     {/* Password field */}
                     <div className={styles.inputGroup}>
                         <label htmlFor="password" className={styles.inputLabel}>Password</label>
                         <div className={styles.passwordInputContainer}>
                             <input type={showPassword ? 'text' : 'password'} id="password" placeholder={isLoginMode ? "Enter your password" : "Password"} value={password} onChange={(e) => {setPassword(e.target.value); if (!isLoginMode) validatePassword(e.target.value);}} minLength="8" required aria-label="Password" disabled={isLoading}/>
                        {password && (
                             <button type="button" className={styles.visibilityToggle} onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                {showPassword ? (
                                         <svg width="24" height="12" viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                             <path d="M24 6C24 6 18.6198 12 12 12C5.35385 12 0 6 0 6C0 6 5.35385 0 12 0C18.6462 0 24 6 24 6ZM12 2.41071C10.0484 2.41071 8.46593 4.01786 8.46593 6C8.46593 7.98214 10.0484 9.58929 12 9.58929C13.9516 9.58929 15.5341 7.98214 15.5341 6C15.5341 4.01786 13.9516 2.41071 12 2.41071Z" fill="#A9A9A9"/>
                                         </svg>
                                     ) : (
                                         <svg width="24" height="12" viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                             <path d="M24 6C24 6 18.6198 12 12 12C5.35385 12 0 6 0 6C0 6 5.35385 0 12 0C18.6462 0 24 6 24 6ZM12 2.41071C10.0484 2.41071 8.46593 4.01786 8.46593 6C8.46593 7.98214 10.0484 9.58929 12 9.58929C13.9516 9.58929 15.5341 7.98214 15.5341 6C15.5341 4.01786 13.9516 2.41071 12 2.41071Z" fill="#A9A9A9"/>
                                             <line x1="2" x2="22" y1="2" y2="10" stroke="#A9A9A9" strokeWidth="2" strokeLinecap="round"/>
                                         </svg>
                                     )}
                                 </button>
                             )}
                         </div>
                     </div>

                     {/* Confirm Password field for signup */}
                     {!isLoginMode && (  
                         <div className={styles.inputGroup}>
                             <label htmlFor="confirm-password" className={styles.inputLabel}>Confirm Password</label>
                             <input type='password' id="confirm-password" placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} minLength="8" required aria-label="Confirm Password" disabled={isLoading}/>
                         </div>
                     )}

                    <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={isLoading}> {isLoading ? (isLoginMode ? 'Logging in...' : 'Creating Account...') : (isLoginMode ? 'Log In' : 'Sign Up')} </button>
                    
                    {/* Forgot password link - only for login mode */}
                    {isLoginMode && ( 
                        <div className={styles.forgotPasswordContainer}>
                            <button type="button" onClick={forgotPasswordHandler} className={styles.forgotPasswordLink} disabled={isLoading}>Forgot password?</button>
                        </div>
                    )}
                    {/* button for amazon */}
                    {/* <button type="button" className={`${styles.btn} ${styles.btnAmazon}`} onClick={handleAmazonLoginClick} disabled={isLoading}> <i className="fab fa-amazon"></i> Continue with Amazon </button>  */}
                 </form>
                 <div className={styles.socialLogin}>
                    <button type="button" className={`${styles.btn} ${styles.btnSocial} ${styles.btnGoogle}`} onClick={handleGoogleLoginClick} disabled={isLoading}>
                        <svg className={styles.socialIcon} width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.04 24.04 0 0 0 0 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                        Continue with Google
                    </button>
                 </div>
                {/* <div className={styles.socialLogin}>
                {/* Amazon Login Button */}
                {/* <button type="button" className={`${styles.btn} ${styles.btnSocial} ${styles.btnAmazon}`} onClick={handleAmazonLoginClick} disabled={isLoading}> 
                    <i className="fab fa-amazon"></i> Continue with Amazon 
                </button>
                </div> */} 
             </>
         );
    }
    // --- End Original Content Logic ---


    // --- JSX Rendering ---
    return (
        <div 
            style={{
                background: `url(${HomepageBackground})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh'
            }}
        >
            <Navbar hideSignUp={true} />
        <div className={styles.authContainerAlt}>
                <div className={styles.authWrapper}>
                {/* Toggle Buttons - Outside Form Card */}
                {!showOnboardingForm && !isForgotPasswordMode && !needsConfirmation && (
                    <div className={styles.toggleContainer}>
                        <button 
                            type="button" 
                            className={`${styles.toggleButton} ${isLoginMode ? styles.toggleButtonActive : ''}`}
                            onClick={() => setIsLoginMode(true)}
                        >
                            {!isLoginMode && (
                                <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18.7515 1.00006H14.7528C14.2168 1.00006 13.7807 1.43618 13.7807 1.97212C13.7807 2.50805 14.2168 2.94418 14.7528 2.94418H17.1775L9.68208 10.4396C9.30128 10.8204 9.30128 11.4333 9.68208 11.8141C9.87085 12.0029 10.1204 12.1005 10.3677 12.1005C10.6151 12.1005 10.8646 12.0062 11.0534 11.8141L18.5488 4.31872V6.48525C18.5488 7.02119 18.9849 7.45731 19.5209 7.45731C20.0568 7.45731 20.4929 7.02119 20.4929 6.48525V2.74449C20.4929 1.78329 19.7097 1 18.7485 1L18.7515 1.00006Z" fill="#737373" stroke="#F7FCFF" strokeWidth="0.833333"/>
                                    <path d="M19.5246 9.50997C18.9887 9.50997 18.5525 9.94609 18.5525 10.482V16.1028C18.5525 17.7279 17.2301 19.0525 15.6028 19.0525H5.39391C3.76873 19.0525 2.44412 17.7301 2.44412 16.1028V5.89391C2.44412 4.26873 3.76659 2.94412 5.39391 2.94412H11.0146C11.5506 2.94412 11.9867 2.508 11.9867 1.97206C11.9867 1.43612 11.5506 1 11.0146 1H5.39391C2.69688 1 0.5 3.19363 0.5 5.89391V16.1061C0.5 18.8031 2.69363 21 5.39391 21H15.6061C18.3031 21 20.5 18.8064 20.5 16.1061V10.4854C20.5 9.94942 20.0639 9.5133 19.5279 9.5133L19.5246 9.50997Z" fill="#737373" stroke="#F7FCFF" strokeWidth="0.833333"/>
                                </svg>
                            )}
                            Log In
                        </button>
                        <button 
                            type="button" 
                            className={`${styles.toggleButton} ${!isLoginMode ? styles.toggleButtonActive : ''}`}
                            onClick={() => setIsLoginMode(false)}
                        >
                            {isLoginMode && (
                                <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18.7515 1.00006H14.7528C14.2168 1.00006 13.7807 1.43618 13.7807 1.97212C13.7807 2.50805 14.2168 2.94418 14.7528 2.94418H17.1775L9.68208 10.4396C9.30128 10.8204 9.30128 11.4333 9.68208 11.8141C9.87085 12.0029 10.1204 12.1005 10.3677 12.1005C10.6151 12.1005 10.8646 12.0062 11.0534 11.8141L18.5488 4.31872V6.48525C18.5488 7.02119 18.9849 7.45731 19.5209 7.45731C20.0568 7.45731 20.4929 7.02119 20.4929 6.48525V2.74449C20.4929 1.78329 19.7097 1 18.7485 1L18.7515 1.00006Z" fill="#737373" stroke="#F7FCFF" strokeWidth="0.833333"/>
                                    <path d="M19.5246 9.50997C18.9887 9.50997 18.5525 9.94609 18.5525 10.482V16.1028C18.5525 17.7279 17.2301 19.0525 15.6028 19.0525H5.39391C3.76873 19.0525 2.44412 17.7301 2.44412 16.1028V5.89391C2.44412 4.26873 3.76659 2.94412 5.39391 2.94412H11.0146C11.5506 2.94412 11.9867 2.508 11.9867 1.97206C11.9867 1.43612 11.5506 1 11.0146 1H5.39391C2.69688 1 0.5 3.19363 0.5 5.89391V16.1061C0.5 18.8031 2.69363 21 5.39391 21H15.6061C18.3031 21 20.5 18.8064 20.5 16.1061V10.4854C20.5 9.94942 20.0639 9.5133 19.5279 9.5133L19.5246 9.50997Z" fill="#737373" stroke="#F7FCFF" strokeWidth="0.833333"/>
                                </svg>
                            )}
                            Sign up
                        </button>
              </div>
                )}
                
                <div className={styles.formCard}>
                    {content}
              </div>
            </div>
            </div>
        </div>
     );
};

export default AuthPage;